import { useState } from 'react';
import type { LoanAccount } from '@coreledger/shared-types';
import { loanAccountsApi } from '../api/loanAccounts';

// A compact, real-LoanAccount-id-bound picker for BankTransactionsPage.tsx's
// "Suggested Ledger" column, once a "Loan Received"/"Loan Repayment (EMI)"
// label is picked — mirrors PartyLedgerPicker.tsx's search UX (this
// codebase's own precedent for "search a small list, click one, save
// immediately") plus a quick-create row borrowed from
// PartySelector.tsx's inline-creation pattern, since unlike parties a
// tenant's loan accounts are rarely pre-populated before this is the first
// place a user records one. Deliberately starts blank on every render (no
// currentLabel/saved prop, unlike PartyLedgerPicker) — the bank entry
// itself doesn't carry a lender name to prefill from, only
// loanTransactionId; see CLAUDE.md's Loans & Capital note.
interface Props {
  loanAccounts: LoanAccount[];
  onSelect: (account: LoanAccount) => void;
  onCreated: (account: LoanAccount) => void;
}

export function LoanAccountSelector({ loanAccounts, onSelect, onCreated }: Props) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [showInline, setShowInline] = useState(false);
  const [inlineName, setInlineName] = useState('');
  const [inlineSaving, setInlineSaving] = useState(false);

  const filtered = search.trim()
    ? loanAccounts.filter(a => a.lenderName.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8)
    : loanAccounts.slice(0, 8);

  async function handleCreate() {
    if (!inlineName.trim()) return;
    setInlineSaving(true);
    try {
      const created = await loanAccountsApi.create({ lenderName: inlineName.trim() });
      onCreated(created);
      onSelect(created);
      setShowInline(false);
      setInlineName('');
      setSearch(created.lenderName);
      setOpen(false);
    } catch {
      alert('Failed to create loan account');
    } finally {
      setInlineSaving(false);
    }
  }

  if (showInline) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', border: '1px solid var(--border-input)', borderRadius: 6, padding: '0.4rem' }}>
        <input
          autoFocus
          value={inlineName}
          onChange={e => setInlineName(e.target.value)}
          placeholder="Lender name, e.g. HDFC Bank"
          style={{ fontSize: '0.78rem', padding: '0.25rem 0.4rem', width: '100%', boxSizing: 'border-box' }}
        />
        <div style={{ display: 'flex', gap: '0.3rem' }}>
          <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: '0.7rem', flex: 1 }} onClick={() => { setShowInline(false); setInlineName(''); }}>Cancel</button>
          <button type="button" className="btn btn-primary btn-sm" style={{ fontSize: '0.7rem', flex: 1 }} disabled={inlineSaving} onClick={handleCreate}>
            {inlineSaving ? '…' : 'Add'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search loan accounts…"
        style={{ fontSize: '0.78rem', padding: '0.25rem 0.4rem', width: '100%', boxSizing: 'border-box' }}
        title="Pick which loan account this transaction belongs to"
      />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, maxHeight: 220, overflowY: 'auto', boxShadow: 'var(--shadow-md)' }}>
          {filtered.map(a => (
            <div
              key={a.id}
              onMouseDown={() => { onSelect(a); setSearch(a.lenderName); setOpen(false); }}
              style={{ padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-alt)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface)')}
            >
              {a.lenderName}
            </div>
          ))}
          <div
            onMouseDown={() => { setShowInline(true); setOpen(false); setInlineName(search); }}
            style={{ padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 600 }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-alt)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface)')}
          >
            + Create New Loan Account…
          </div>
        </div>
      )}
    </div>
  );
}
